import { ReactChild } from 'react'

import { Root, Indicator } from '@radix-ui/react-checkbox'
import { css, styled } from 'styled-components'

import Spinner from 'core/components/lib/Spinner'
import { PendingEventHandler, usePendingCallback } from 'core/hooks'
import { Field } from 'core/hooks/useForm'
import {
  onSurface,
  onSurfaceDim,
  onSurfaceDisabled,
  onSurfaceVariant,
  outline,
  surfaceBright,
  textInputBoxShadow,
} from 'core/styles'

type CheckboxVariant = 'default' | 'compact' | 'compact-white-bg' | 'readonly'

type CheckboxProps<T extends boolean | 'indeterminate'> = Omit<Field<T>, 'onChange'> & {
  className?: string
  /** Calls the provided function when activated. Return a promise to show a spinner while the promise resolves. */
  onChange?: PendingEventHandler<T>
  /** The text to display alongside the checkbox. */
  label?: ReactChild
  /** The text to display underneath the checkbox and label. */
  sublabel?: ReactChild
  /** The text for accessibility for when `label` is not used. */
  ariaLabel?: string
  /** Replaces the checkbox with a spinner while true. */
  isLoading?: boolean
  /** The variant of the checkbox. */
  variant?: CheckboxVariant
}

/** A single Checkbox with a label and sublabel. */
const Checkbox = <T extends boolean | 'indeterminate'>({
  value,
  name,
  disabled,
  onChange: _onChange,
  label,
  sublabel,
  ariaLabel,
  isLoading,
  className,
  variant = 'default',
}: CheckboxProps<T>) => {
  const [isPending, onChange] = usePendingCallback(_onChange)

  return (
    <Box $disabled={disabled ?? variant === 'readonly'} $variant={variant} className={className}>
      {isPending || isLoading ?
        <Spinner height='20px' width='20px' />
      : <StyledRoot
          $variant={variant}
          aria-label={ariaLabel}
          checked={value}
          disabled={disabled ?? variant === 'readonly'}
          id={name}
          name={name}
          onCheckedChange={onChange}
          value={value.toString()}
        >
          <StyledIndicator forceMount />
        </StyledRoot>
      }
      {!!label && (
        <Label $variant={variant} htmlFor={name}>
          {label}
        </Label>
      )}
      {!!sublabel && <SubLabel $variant={variant}>{sublabel}</SubLabel>}
    </Box>
  )
}

export default Checkbox

const Box = styled.div<{ $disabled: boolean | undefined; $variant?: CheckboxVariant }>`
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: 0 8px;
  align-items: center;

  button,
  label {
    cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  }
`

const compactRootCss = css`
  border-radius: 4px;
  width: 17px;
  height: 17px;

  &[data-state='checked'] {
    & > span {
      right: 9px !important;
      bottom: 4px !important;
      border-right: 1px solid ${surfaceBright} !important;
      border-bottom: 1px solid ${surfaceBright} !important;
      width: 5px !important;
      height: 9px !important;
    }
  }
`

const StyledRoot = styled(Root)<{ $variant?: CheckboxVariant }>`
  box-sizing: border-box;
  position: relative;
  border: 2px solid ${onSurfaceVariant};
  border-radius: 10%;
  background-color: ${surfaceBright};
  width: 20px;
  height: 20px;

  &[data-state='checked'] {
    border-color: ${(p) => p.theme.primary};
    background-color: ${(p) => p.theme.secondary};
  }

  &[data-disabled] {
    border-color: ${onSurfaceDisabled};
    background-color: ${onSurfaceDisabled};
  }

  ${(p) =>
    p.$variant === 'compact' &&
    css`
      ${compactRootCss};
      border: 1px solid ${outline};
      background-color: ${surfaceBright};

      &[data-state='checked'] {
        border-color: ${onSurface};
        background-color: ${onSurface};
      }
    `}

  ${(p) =>
    p.$variant === 'compact-white-bg' &&
    css`
      ${compactRootCss};
      border: 1px solid ${outline};
      box-shadow: ${textInputBoxShadow};
      background-color: ${surfaceBright};

      &[data-state='checked'] {
        border-color: ${onSurface};
        background-color: ${onSurface};
      }
    `}

  ${(p) =>
    p.$variant === 'readonly' &&
    css`
      ${compactRootCss};
      border: 1px solid ${onSurfaceDim};
      background-color: ${onSurfaceDim};

      &[data-state='checked'] {
        border-color: ${onSurfaceDim};
        background-color: ${onSurfaceDim};
      }

      &[data-disabled] {
        border-color: ${onSurfaceDim};
        background-color: ${onSurfaceDim};
      }
    `}
`

const StyledIndicator = styled(Indicator)`
  box-sizing: border-box;
  position: absolute;
  right: 9.5px;
  bottom: 4px;
  transform: rotate(45deg);
  transform-origin: bottom right;
  opacity: 0;
  border-right: 2px solid white;
  border-bottom: 2px solid white;
  border-radius: 10%;
  width: 0;
  height: 0;

  &[data-state='checked'] {
    transition: all 0.1s ease-in;
    opacity: 1;
    width: 6px;
    height: 10px;
  }

  &[data-state='indeterminate'] {
    right: 3px;
    bottom: 7px;
    transform: none;
    transition: all 0.1s ease-in;
    opacity: 1;
    border-color: ${onSurfaceVariant};
    width: 10px;
    height: 0;

    &[data-disabled] {
      border-color: white;
    }
  }
`

export const SubLabel = styled.span<{ $variant?: CheckboxVariant }>`
  grid-column: 2;
  margin-bottom: 8px;
  color: ${onSurfaceVariant};
  font-size: 14px;

  ${(p) =>
    (p.$variant === 'compact' || p.$variant === 'compact-white-bg' || p.$variant === 'readonly') &&
    `
      margin: 0;
      color: ${onSurfaceVariant};
      font-weight: 400;
    `}
`

const Label = styled.label<{ $variant?: CheckboxVariant }>`
  ${(p) =>
    p.$variant === 'compact' || p.$variant === 'compact-white-bg' || p.$variant === 'readonly' ?
      `
      color: ${onSurface};
      font-size: 14px;
      font-weight: 400;
    `
    : `
      color: inherit;
      font-size: inherit;
    `}
`
